import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MEXICAN_STATES } from "@/lib/mexican-states";
import { Eye, EyeOff } from "lucide-react";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Acceder o registrarse — Brulo" },
      { name: "description", content: "Inicia sesión o crea tu cuenta de atleta en Brulo. Únete a la comunidad run · ride · gather." },
      { property: "og:title", content: "Acceder o registrarse — Brulo" },
      { property: "og:description", content: "Inicia sesión o crea tu cuenta de atleta en Brulo." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();

  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("+52 ");
  const [city, setCity] = useState("Aguascalientes");
  const [estado, setEstado] = useState("Aguascalientes");
  const [disciplinas, setDisciplinas] = useState<string[]>([]);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const toggleDisciplina = (d: string) =>
    setDisciplinas((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const DISCIPLINAS = [
    { v: "running", l: "Running", e: "🏃" },
    { v: "ciclismo", l: "Ciclismo", e: "🚴" },
    { v: "trail", l: "Trail", e: "🏔️" },
    { v: "caminata", l: "Caminata", e: "🚶" },
    { v: "triatlon", l: "Triatlón", e: "🏊" },
    { v: "otro", l: "Otro", e: "💪" },
  ];
  const [loading, setLoading] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (mode === "signup") {
      const errors: Record<string, string> = {};
      if (!fullName.trim()) errors.fullName = "El nombre es obligatorio";
      if (!phone.trim() || phone.trim() === "+52") errors.phone = "El teléfono es obligatorio";
      if (!city.trim()) errors.city = "La ciudad es obligatoria";
      if (!estado.trim()) errors.estado = "El estado es obligatorio";
      if (!email.trim()) errors.email = "El email es obligatorio";
      if (!confirmEmail.trim()) errors.confirmEmail = "Confirma tu correo electrónico";
      if (email.trim() && confirmEmail.trim() && email.trim() !== confirmEmail.trim()) {
        errors.confirmEmail = "Los correos no coinciden";
      }
      if (!password.trim()) errors.password = "La contraseña es obligatoria";
      if (!privacyAccepted) errors.privacy = "Debes aceptar el Aviso de Privacidad para continuar";
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        toast.error("Por favor completa todos los campos obligatorios");
        return;
      }
    }

    setLoading(true);
    try {
      if (mode === "signup") {
        const metaData: Record<string, any> = {
          full_name: fullName,
          role: "runner",
          city,
          phone: phone.trim() || null,
          estado,
          disciplinas,
        };

        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin + "/dashboard",
            data: metaData,
          },
        });
        if (error) throw error;

        toast.success("¡Casi listo! Confirma tu correo para entrar.");
        navigate({ to: "/check-email", search: { email: email.trim() } });
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          const msg = error.message.toLowerCase();
          if (msg.includes("not confirmed") || msg.includes("confirm")) {
            toast.error("Confirma tu email para continuar.", {
              action: {
                label: "Reenviar confirmación",
                onClick: async () => {
                  const { error: rErr } = await supabase.auth.resend({
                    type: "signup",
                    email,
                    options: { emailRedirectTo: window.location.origin + "/dashboard" },
                  });
                  if (rErr) toast.error(rErr.message);
                  else toast.success("Te reenviamos el correo de confirmación");
                },
              },
            });
            return;
          }
          throw error;
        }
        toast.success("Sesión iniciada");
        navigate({ to: "/dashboard" });
      }
    } catch (err: any) {
      const raw = (err?.message ?? "").toString();
      let friendly = raw || "Error de autenticación";
      if (/database error saving new user/i.test(raw)) {
        friendly =
          "No pudimos crear tu cuenta en este momento. Revisa tus datos e inténtalo de nuevo en unos minutos.";
      } else if (/weak password|known to be weak|pwned/i.test(raw)) {
        friendly = "Esta contraseña es muy común o insegura. Usa una contraseña más larga y única.";
      } else if (/already registered|already exists|user already/i.test(raw)) {
        friendly = "Este email ya está registrado. Inicia sesión o usa otro correo.";
      }
      toast.error(friendly);
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async () => {
    if (!email) {
      toast.error("Escribe tu email primero");
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/auth",
    });
    if (error) toast.error(error.message);
    else toast.success("Te enviamos un correo para restablecer tu contraseña");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 py-8 sm:py-12 brulo-grain">
      <div className="w-full max-w-md">
        <Link to="/" className="font-display text-3xl tracking-widest block text-center mb-10">
          BRULO<span className="text-primary">.</span>
        </Link>

        <div className="bg-card border border-border rounded-lg p-8">
          <h1 className="font-display text-3xl mb-8 text-center">
            {mode === "signin" ? "Bienvenido de vuelta" : "Únete a Brulo"}
          </h1>

          <form onSubmit={submit} className="space-y-4">
            {mode === "signup" && (
              <>
                <Input
                  label="Nombre completo"
                  value={fullName}
                  onChange={(v) => { setFullName(v); clearFieldError("fullName"); }}
                  required
                  error={fieldErrors.fullName}
                />
                <div className="space-y-1">
                  <Input
                    label="Teléfono (WhatsApp)"
                    type="tel"
                    value={phone}
                    onChange={(v) => { setPhone(v); clearFieldError("phone"); }}
                    placeholder="+52 449 000 0000"
                    required
                    error={fieldErrors.phone}
                  />
                  <p className="text-xs text-muted-foreground">
                    Te enviaremos notificaciones de tus eventos por WhatsApp.
                  </p>
                </div>
                <Input
                  label="Ciudad"
                  value={city}
                  onChange={(v) => { setCity(v); clearFieldError("city"); }}
                  required
                  error={fieldErrors.city}
                />
                <div>
                  <label className="text-sm text-muted-foreground block mb-1.5">
                    Estado<span className="text-destructive ml-0.5">*</span>
                  </label>
                  <select
                    value={estado}
                    onChange={(e) => { setEstado(e.target.value); clearFieldError("estado"); }}
                    className={`w-full px-4 py-3 min-h-11 bg-input border rounded-md focus:outline-none focus:border-primary transition ${fieldErrors.estado ? "border-destructive" : "border-border"}`}
                    required
                  >
                    {MEXICAN_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {fieldErrors.estado && (
                    <p className="text-xs text-destructive mt-1">{fieldErrors.estado}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-muted-foreground block mb-1">
                    ¿Qué disciplinas practicas?
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Solo para conocerte mejor — puedes seleccionar varias
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {DISCIPLINAS.map((d) => {
                      const active = disciplinas.includes(d.v);
                      return (
                        <button
                          type="button"
                          key={d.v}
                          onClick={() => toggleDisciplina(d.v)}
                          className={`py-2 px-3 rounded-md border text-xs uppercase tracking-wider ${
                            active
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border text-muted-foreground"
                          }`}
                        >
                          <span className="mr-1">{d.e}</span>{d.l}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(v) => { setEmail(v); clearFieldError("email"); }}
              required
              error={fieldErrors.email}
            />
            {mode === "signup" && (
              <Input
                label="Confirmar correo electrónico"
                type="email"
                value={confirmEmail}
                onChange={(v) => { setConfirmEmail(v); clearFieldError("confirmEmail"); }}
                onPaste={(e) => { e.preventDefault(); }}
                required
                error={fieldErrors.confirmEmail}
              />
            )}

            <div>
              <PasswordInput
                label="Contraseña"
                value={password}
                onChange={(v) => { setPassword(v); clearFieldError("password"); }}
                show={showPassword}
                onToggle={() => setShowPassword((s) => !s)}
                required
                error={fieldErrors.password}
              />
              {mode === "signin" && (
                <button
                  type="button"
                  onClick={forgotPassword}
                  className="text-xs text-muted-foreground hover:text-primary mt-2"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              )}
            </div>

            {mode === "signup" && (
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => {
                      setPrivacyAccepted(e.target.checked);
                      clearFieldError("privacy");
                    }}
                    className={`mt-1 w-4 h-4 rounded border accent-primary cursor-pointer ${fieldErrors.privacy ? "border-destructive" : "border-border"}`}
                  />
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    He leído y acepto el{" "}
                    <Link
                      to="/privacidad"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Aviso de Privacidad
                    </Link>
                  </span>
                </label>
                {fieldErrors.privacy && (
                  <p className="text-xs text-destructive mt-2 ml-7">{fieldErrors.privacy}</p>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full min-h-12 px-3 py-3 rounded-md bg-primary text-primary-foreground text-sm sm:text-base font-semibold tracking-wide hover:opacity-90 disabled:opacity-50 whitespace-normal break-words text-center leading-tight"
            >
              {loading ? "..." : mode === "signup" ? "Unirme a Brulo" : "Entrar"}
            </button>

            <p className="text-center text-sm text-muted-foreground pt-2">
              {mode === "signin" ? (
                <>
                  ¿Aún no tienes cuenta?{" "}
                  <button
                    type="button"
                    onClick={() => setMode("signup")}
                    className="text-primary hover:underline"
                  >
                    Únete a Brulo
                  </button>
                </>
              ) : (
                <>
                  ¿Ya tienes cuenta?{" "}
                  <button
                    type="button"
                    onClick={() => setMode("signin")}
                    className="text-primary hover:underline"
                  >
                    Inicia sesión
                  </button>
                </>
              )}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  required,
  placeholder,
  error,
  onPaste,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  error?: string;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <div>
      <label className="text-sm text-muted-foreground block mb-1.5">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onPaste={onPaste}
        className={`w-full px-4 py-3 min-h-11 bg-input border rounded-md focus:outline-none focus:border-primary transition ${error ? "border-destructive" : "border-border"}`}
      />
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

function PasswordInput({
  label,
  value,
  onChange,
  show,
  onToggle,
  required,
  placeholder,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
  required?: boolean;
  placeholder?: string;
  error?: string;
}) {
  return (
    <div>
      <label className="text-sm text-muted-foreground block mb-1.5">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          required={required}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full px-4 py-3 min-h-11 bg-input border rounded-md focus:outline-none focus:border-primary transition pr-10 ${error ? "border-destructive" : "border-border"}`}
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary focus:outline-none"
          aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
